import { normalizeUsStateCode } from './usStateNormalize';

const PHOTON_REVERSE_URL = 'https://photon.komoot.io/reverse';
const GEOLOCATION_TIMEOUT_MS = 20_000;

export type DeviceLocationUnavailableReason =
  | 'unsupported'
  | 'permission_denied'
  | 'position_unavailable'
  | 'timeout'
  | 'reverse_geocode_failed'
  | 'non_us'
  | 'incomplete';

export type DeviceLocationPrefill = {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  /** Hint values stored for state-mismatch checks (same FHIR fields as legacy IP prefill). */
  derivedCity: string;
  derivedState: string;
  /** True when street and ZIP were both resolved; user may still need to edit. */
  isComplete: boolean;
};

export type DeviceLocationResult =
  | ({ available: true } & DeviceLocationPrefill)
  | {
      available: false;
      unavailableReason: DeviceLocationUnavailableReason;
    };

type PhotonReverseFeature = {
  properties?: {
    housenumber?: string;
    street?: string;
    name?: string;
    city?: string;
    district?: string;
    locality?: string;
    county?: string;
    state?: string;
    postcode?: string;
    country?: string;
    countrycode?: string;
    type?: string;
    osm_key?: string;
  };
};

const PHOTON_REVERSE_LIMIT = 8;

function normalizeUsZip(raw: string | undefined): string {
  if (!raw?.trim()) return '';
  const match = raw.trim().match(/^(\d{5})(?:-\d{4})?$/);
  return match?.[1] ?? '';
}

function resolvePhotonStreet(p: NonNullable<PhotonReverseFeature['properties']>): string {
  const numbered = [p.housenumber?.trim(), p.street?.trim()].filter(Boolean).join(' ').trim();
  if (numbered) return numbered;
  if (p.street?.trim()) return p.street.trim();
  if (p.osm_key === 'highway' || p.type === 'street') {
    return p.name?.trim() ?? '';
  }
  return '';
}

function resolvePhotonCity(p: NonNullable<PhotonReverseFeature['properties']>): string {
  return p.city?.trim() || p.district?.trim() || p.locality?.trim() || '';
}

function isUsPhotonFeature(p: NonNullable<PhotonReverseFeature['properties']>): boolean {
  return (
    p.countrycode?.toUpperCase() === 'US' ||
    p.country?.trim().toLowerCase() === 'united states'
  );
}

function prefillCompletenessScore(prefill: DeviceLocationPrefill): number {
  let score = 0;
  if (prefill.street) score += 4;
  if (prefill.postalCode) score += 2;
  if (prefill.city) score += 1;
  if (prefill.state) score += 1;
  if (prefill.isComplete) score += 8;
  return score;
}

/** Parse Photon reverse-geocode feature into US address fields (full or partial). */
export function parsePhotonReverseFeature(
  feature: PhotonReverseFeature | undefined,
): DeviceLocationPrefill | null {
  const p = feature?.properties;
  if (!p || !isUsPhotonFeature(p)) return null;

  const state = normalizeUsStateCode(p.state);
  const city = resolvePhotonCity(p);
  if (!city || !state) return null;

  const street = resolvePhotonStreet(p);
  const postalCode = normalizeUsZip(p.postcode);
  const isComplete = Boolean(street && postalCode);

  return {
    street,
    city,
    state,
    postalCode,
    derivedCity: city,
    derivedState: state,
    isComplete,
  };
}

/** Pick the best US address from multiple Photon reverse-geocode features. */
export function pickBestPhotonReversePrefill(
  features: PhotonReverseFeature[] | undefined,
): DeviceLocationPrefill | null {
  if (!features?.length) return null;

  let best: DeviceLocationPrefill | null = null;
  let bestScore = -1;
  for (const feature of features) {
    const parsed = parsePhotonReverseFeature(feature);
    if (!parsed) continue;
    const score = prefillCompletenessScore(parsed);
    if (score > bestScore) {
      best = parsed;
      bestScore = score;
    }
  }
  return best;
}

function mapGeolocationError(code: number): DeviceLocationUnavailableReason {
  if (code === 1) return 'permission_denied';
  if (code === 2) return 'position_unavailable';
  if (code === 3) return 'timeout';
  return 'position_unavailable';
}

function getCurrentPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new Error('unsupported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: GEOLOCATION_TIMEOUT_MS,
      maximumAge: 60_000,
    });
  });
}

async function reverseGeocode(lat: number, lon: number): Promise<PhotonReverseFeature[]> {
  const params = new URLSearchParams({
    lat: String(lat),
    lon: String(lon),
    limit: String(PHOTON_REVERSE_LIMIT),
    lang: 'en',
  });
  const res = await fetch(`${PHOTON_REVERSE_URL}?${params}`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) return [];
  const data = (await res.json()) as { features?: PhotonReverseFeature[] };
  return data.features ?? [];
}

/**
 * Browser Geolocation API + Photon reverse geocode (US addresses only).
 * Requires user permission and HTTPS (or localhost).
 */
export async function fetchDeviceLocationPrefill(): Promise<DeviceLocationResult> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return { available: false, unavailableReason: 'unsupported' };
  }

  let position: GeolocationPosition;
  try {
    position = await getCurrentPosition();
  } catch (err) {
    const geoErr = err as GeolocationPositionError | undefined;
    if (geoErr?.code != null) {
      return { available: false, unavailableReason: mapGeolocationError(geoErr.code) };
    }
    if (err instanceof Error && err.message === 'unsupported') {
      return { available: false, unavailableReason: 'unsupported' };
    }
    return { available: false, unavailableReason: 'position_unavailable' };
  }

  const { latitude, longitude } = position.coords;
  let features: PhotonReverseFeature[];
  try {
    features = await reverseGeocode(latitude, longitude);
  } catch {
    return { available: false, unavailableReason: 'reverse_geocode_failed' };
  }

  if (!features.length) {
    return { available: false, unavailableReason: 'reverse_geocode_failed' };
  }

  const parsed = pickBestPhotonReversePrefill(features);
  if (!parsed) {
    const firstCountry = features[0]?.properties?.countrycode?.toUpperCase();
    if (firstCountry && firstCountry !== 'US') {
      return { available: false, unavailableReason: 'non_us' };
    }
    return { available: false, unavailableReason: 'incomplete' };
  }

  return { available: true, ...parsed };
}
